import { useState, useEffect } from 'react';
import { getCategories, getBudget, api } from '../api';
import { styles } from '../styles';
import { BackIcon } from '../components/Icons';

function formatMonthDisplay(year, month) {
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function BudgetSettingsScreen({ month, onBack, onManageCategories, onAlertSettings }) {
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [projectTotals, setProjectTotals] = useState({});
  const [subBudgets, setSubBudgets] = useState({});
  const [projectCategories, setProjectCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    Promise.all([
      getCategories(),
      getBudget(month.year, month.month)
    ])
      .then(([cats, budgetData]) => {
        const activeCats = cats.filter(c => c.is_active);
        setCategories(activeCats);

        const budgetMap = {};
        budgetData.by_category.forEach(b => {
          budgetMap[b.category.id] = b.monthly_limit;
        });
        setBudgets(budgetMap);

        // Project envelopes
        const projTotals = {};
        const subBudgetMap = {};
        budgetData.project_categories.forEach(proj => {
          if (proj.total_limit != null) projTotals[proj.category.id] = proj.total_limit;
          proj.sub_categories.forEach(sub => {
            if (sub.budget_limit != null) subBudgetMap[sub.category.id] = sub.budget_limit;
          });
        });
        setProjectTotals(projTotals);
        setSubBudgets(subBudgetMap);
        setProjectCategories(budgetData.project_categories);

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [month.year, month.month]);

  const handleSaveMonthly = async (categoryId, value) => {
    const amount = parseFloat(value) || 0;
    setSaving(categoryId);
    try {
      await api.put(`/budget/categories/${categoryId}`, {
        category_id: categoryId,
        monthly_limit: amount,
      });
      setBudgets(prev => ({ ...prev, [categoryId]: amount }));
    } catch (e) {
      console.error('Failed to save budget', e);
    } finally {
      setSaving(null);
    }
  };

  const handleSaveProjectTotal = async (categoryId, value) => {
    const amount = parseFloat(value) || 0;
    setSaving(`proj-${categoryId}`);
    try {
      await api.put(`/budget/categories/${categoryId}`, {
        category_id: categoryId,
        monthly_limit: 0,
        total_limit: amount,
      });
      setProjectTotals(prev => ({ ...prev, [categoryId]: amount }));
    } catch (e) {
      console.error('Failed to save project budget', e);
    } finally {
      setSaving(null);
    }
  };

  const handleSaveSubBudget = async (categoryId, value, parentProjectId) => {
    const amount = parseFloat(value) || 0;
    setSaving(`sub-${categoryId}`);
    try {
      await api.put(`/budget/categories/${categoryId}`, {
        category_id: categoryId,
        monthly_limit: amount,
      });

      const updatedSubBudgets = { ...subBudgets, [categoryId]: amount };
      setSubBudgets(updatedSubBudgets);

      // Auto-update project total to sum of all sub-category budgets
      const proj = projectCategories.find(p => p.category.id === parentProjectId);
      if (proj) {
        const newTotal = proj.sub_categories.reduce((sum, sub) => {
          return sum + (parseFloat(updatedSubBudgets[sub.category.id]) || 0);
        }, 0);
        await api.put(`/budget/categories/${parentProjectId}`, {
          category_id: parentProjectId,
          monthly_limit: 0,
          total_limit: newTotal,
        });
        setProjectTotals(prev => ({ ...prev, [parentProjectId]: newTotal }));
      }
    } catch (e) {
      console.error('Failed to save sub-category budget', e);
    } finally {
      setSaving(null);
    }
  };

  const monthlyCats = categories.filter(c => c.parent_id === null && c.budget_type !== 'project');

  if (loading) {
    return (
      <div style={{ ...styles.screen, ...styles.centered }}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Budget Settings</h1>
        <div style={{ width: 20 }} />
      </header>

      <p style={settingsStyles.description}>
        Budget limits for {formatMonthDisplay(month.year, month.month)}
      </p>

      <div style={{ display: 'flex', gap: 25, marginBottom: 5 }}>
        <button style={settingsStyles.manageLink} onClick={onManageCategories}>
          Manage Categories →
        </button>
        <button style={settingsStyles.manageLink} onClick={onAlertSettings}>
          Alert Settings →
        </button>
      </div>

      {/* Monthly budgets */}
      <div style={settingsStyles.list}>
        {monthlyCats.map(cat => (
          <div key={cat.id} style={settingsStyles.item}>
            <span style={settingsStyles.categoryName}>{cat.icon} {cat.name}</span>
            <div style={settingsStyles.inputWrapper}>
              <span style={settingsStyles.currency}>$</span>
              <input
                type="number"
                inputMode="decimal"
                style={settingsStyles.input}
                value={budgets[cat.id] || ''}
                placeholder="0"
                onChange={(e) => setBudgets(prev => ({ ...prev, [cat.id]: e.target.value }))}
                onBlur={(e) => handleSaveMonthly(cat.id, e.target.value)}
              />
              {saving === cat.id && <span style={settingsStyles.savingIndicator}>...</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Project envelopes */}
      {projectCategories.length > 0 && (
        <>
          <p style={{ ...settingsStyles.description, marginTop: 24, marginBottom: 8, fontWeight: 600 }}>
            Project Envelopes
          </p>
          <div style={settingsStyles.list}>
            {projectCategories.map(proj => (
              <div key={proj.category.id} style={settingsStyles.projectCard}>
                {/* Project root total */}
                <div style={settingsStyles.item}>
                  <div>
                    <span style={settingsStyles.categoryName}>{proj.category.icon} {proj.category.name}</span>
                    {proj.sub_categories.length > 0 && (
                      <span style={settingsStyles.autoLabel}> · auto</span>
                    )}
                  </div>
                  <div style={settingsStyles.inputWrapper}>
                    <span style={settingsStyles.currency}>$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      style={{
                        ...settingsStyles.input,
                        ...(proj.sub_categories.length > 0 ? settingsStyles.inputAuto : {}),
                      }}
                      value={projectTotals[proj.category.id] || ''}
                      placeholder="Total"
                      readOnly={proj.sub_categories.length > 0}
                      onChange={(e) => {
                        if (proj.sub_categories.length === 0) {
                          setProjectTotals(prev => ({ ...prev, [proj.category.id]: e.target.value }));
                        }
                      }}
                      onBlur={(e) => {
                        if (proj.sub_categories.length === 0) {
                          handleSaveProjectTotal(proj.category.id, e.target.value);
                        }
                      }}
                    />
                    {saving === `proj-${proj.category.id}` && <span style={settingsStyles.savingIndicator}>...</span>}
                  </div>
                </div>
                {/* Sub-category budgets */}
                {proj.sub_categories.map(sub => (
                  <div key={sub.category.id} style={settingsStyles.subItem}>
                    <span style={settingsStyles.subCatName}>{sub.category.icon} {sub.category.name}</span>
                    <div style={settingsStyles.inputWrapper}>
                      <span style={settingsStyles.currency}>$</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        style={settingsStyles.input}
                        value={subBudgets[sub.category.id] || ''}
                        placeholder="0"
                        onChange={(e) => setSubBudgets(prev => ({ ...prev, [sub.category.id]: e.target.value }))}
                        onBlur={(e) => handleSaveSubBudget(sub.category.id, e.target.value, proj.category.id)}
                      />
                      {saving === `sub-${sub.category.id}` && <span style={settingsStyles.savingIndicator}>...</span>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const settingsStyles = {
  description: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 12,
  },
  manageLink: {
    fontSize: 14,
    fontWeight: 500,
    color: '#6366f1',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    marginBottom: 20,
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 14,
    borderRadius: 12,
    border: '1px solid #e2e8f0',
  },
  projectCard: {
    borderRadius: 12,
    border: '1px solid #c7d2fe',
    overflow: 'hidden',
  },
  subItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    padding: '10px 14px 10px 24px',
    borderTop: '1px solid #e2e8f0',
  },
  categoryName: {
    fontSize: 15,
    fontWeight: 500,
    color: '#1e293b',
  },
  subCatName: {
    fontSize: 14,
    color: '#475569',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  currency: {
    position: 'absolute',
    left: 10,
    color: '#64748b',
    fontSize: 15,
  },
  input: {
    width: 100,
    padding: '8px 10px 8px 24px',
    fontSize: 15,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    textAlign: 'right',
    outline: 'none',
  },
  savingIndicator: {
    marginLeft: 8,
    color: '#64748b',
  },
  autoLabel: {
    fontSize: 12,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  inputAuto: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    cursor: 'default',
  },
};
